import React, { useEffect, useState } from "react";
import { X, Info } from "lucide-react";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import "./CriarNotificacaoModal.css";
import { useGlobalModal } from "../GlobalModalProvider/GlobalModalProvider";
import { showApiErrorModal } from "@/utils/common/apiErrorHelper";

export const NOTIFICATION_TYPES = {
  WELCOME: "WELCOME",
  PAYMENT_REMINDER: "PAYMENT_REMINDER",
  PAYMENT_OVERDUE: "PAYMENT_OVERDUE",
  CONTRACT_ENDING: "CONTRACT_ENDING",
  CONTRACT_EXPIRED: "CONTRACT_EXPIRED",
  ADHOC: "ADHOC",
} as const;

export type NotificationType = keyof typeof NOTIFICATION_TYPES;

export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  WELCOME: "Boas-vindas",
  PAYMENT_REMINDER: "Lembrete de Pagamento",
  PAYMENT_OVERDUE: "Pagamento em Atraso",
  CONTRACT_ENDING: "Contrato Encerrando",
  CONTRACT_EXPIRED: "Contrato Expirado",
  ADHOC: "Ad-hoc (Campanha)",
};

export const NOTIFICATION_TYPE_DESCRIPTIONS: Record<NotificationType, string> = {
  WELCOME: "Enviado automaticamente quando um novo cliente é cadastrado.",
  PAYMENT_REMINDER: "Enviado X dias antes do vencimento do pagamento.",
  PAYMENT_OVERDUE: "Enviado quando um pagamento está em atraso.",
  CONTRACT_ENDING: "Enviado X dias antes do término do contrato.",
  CONTRACT_EXPIRED: "Enviado quando o contrato expira.",
  ADHOC: "Notificação manual para campanhas ou comunicados específicos.",
};

export const TYPE_DAYS_OFFSET_CONFIG: Record<
  NotificationType,
  { show: boolean; label: string; default: number }
> = {
  WELCOME: { show: false, label: "", default: 0 },
  PAYMENT_REMINDER: { show: true, label: "Dias antes do vencimento", default: 7 },
  PAYMENT_OVERDUE: { show: false, label: "", default: 0 },
  CONTRACT_ENDING: { show: true, label: "Dias antes do término", default: 30 },
  CONTRACT_EXPIRED: { show: false, label: "", default: 0 },
  ADHOC: { show: false, label: "", default: 0 },
};

interface DefaultBody {
  title: string;
  intro: string;
  message: string;
  conclusion: string;
}

interface CriarNotificacaoModalProps {
  onClose: () => void;
  onCreated?: () => void;
}

function isValidOptionalUrl(value: string) {
  if (!value.trim()) return true;
  try {
    new URL(value.trim());
    return true;
  } catch {
    return false;
  }
}

export function CriarNotificacaoModal({ onClose, onCreated }: CriarNotificacaoModalProps) {
  const [selectedType, setSelectedType] = useState<NotificationType | "">("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [introduction, setIntroduction] = useState("");
  const [mainMessage, setMainMessage] = useState("");
  const [conclusion, setConclusion] = useState("");
  const [contactLink, setContactLink] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [daysOffset, setDaysOffset] = useState<number>(0);
  const [defaultBodies, setDefaultBodies] = useState<Record<string, DefaultBody>>({});

  const userContext = useUser();
  const userData = userContext?.userData;
  const [submitting, setSubmitting] = useState(false);
  const modal = useGlobalModal();

  const resetForm = () => {
    setSelectedType("");
    setTitle("");
    setDescription("");
    setIntroduction("");
    setMainMessage("");
    setConclusion("");
    setContactLink("");
    setImageUrl("");
    setDaysOffset(0);
  };

  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadDefaults() {
      if (!userData?.auth) return;
      try {
        const resp = await api.get("/notifications/templates/defaults", {
          headers: { Authorization: `Bearer ${userData.auth}` },
        });
        const data = (resp as any)?.data ?? resp;
        if (mounted && data) {
          setDefaultBodies(data);
        }
      } catch (err) {
        console.error("Erro ao buscar bodies padrão:", err);
      }
    }

    loadDefaults();
    return () => {
      mounted = false;
    };
  }, [userData?.auth]);

  useEffect(() => {
    if (selectedType && selectedType !== "ADHOC" && defaultBodies[selectedType]) {
      const defaults = defaultBodies[selectedType];
      setTitle(defaults.title || "");
      setIntroduction(defaults.intro || "");
      setMainMessage(defaults.message || "");
      setConclusion(defaults.conclusion || "");
    }

    if (selectedType) {
      setDaysOffset(TYPE_DAYS_OFFSET_CONFIG[selectedType].default);
    }
  }, [selectedType, defaultBodies]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    if (!selectedType) {
      modal?.show({
        type: "warning",
        title: "Tipo obrigatório",
        message: "Selecione um tipo de notificação.",
        autoClose: 3000,
      });
      setSubmitting(false);
      return;
    }

    if (!title.trim()) {
      modal?.show({
        type: "warning",
        title: "Campos vazios",
        message: "Preencha o título da notificação.",
        autoClose: 3000,
      });
      setSubmitting(false);
      return;
    }

    if (!mainMessage.trim()) {
      modal?.show({
        type: "warning",
        title: "Campos vazios",
        message: "Preencha a mensagem principal.",
        autoClose: 3000,
      });
      setSubmitting(false);
      return;
    }

    if (!isValidOptionalUrl(imageUrl)) {
      modal?.show({
        type: "warning",
        title: "Imagem inválida",
        message: "Informe uma URL válida para a imagem.",
        autoClose: 3000,
      });
      setSubmitting(false);
      return;
    }

    if (!isValidOptionalUrl(contactLink)) {
      modal?.show({
        type: "warning",
        title: "Link inválido",
        message: "Informe uma URL válida para o link de contato.",
        autoClose: 3000,
      });
      setSubmitting(false);
      return;
    }

    try {
      const token = userData?.auth;
      const coworkingId =
        (userData as any)?.coworkingId ?? (userData as any)?.user?.coworkingId ?? null;

      if (!coworkingId) {
        modal?.show({
          type: "error",
          title: "Erro",
          message: "Coworking não identificado. Faça login novamente.",
          autoClose: 3000,
        });
        setSubmitting(false);
        return;
      }

      const payload = {
        coworkingId,
        title: title.trim(),
        description: description.trim() || undefined,
        type: selectedType,
        imageUrl: imageUrl.trim() || undefined,
        intro: introduction.trim() || undefined,
        message: mainMessage.trim(),
        conclusion: conclusion.trim() || undefined,
        contactLink: contactLink.trim() || undefined,
        daysOffset: Math.max(0, Math.floor(Number(daysOffset) || 0)),
        active: true,
      };

      await api.post("/notifications/templates", payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      modal?.show({
        type: "success",
        title: "Sucesso",
        message: "Notificação criada com sucesso.",
        autoClose: 3000,
      });
      resetForm();
      onCreated?.();
      onClose();
    } catch (err: any) {
      console.error("Erro ao criar template:", err);
      showApiErrorModal(modal, err, {
        title: "Erro ao criar notificacao",
        fallbackMessage: "Erro inesperado ao criar notificacao.",
        autoClose: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const showDaysOffset = selectedType && TYPE_DAYS_OFFSET_CONFIG[selectedType]?.show;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close" onClick={onClose}>
          <X size={20} />
        </button>

        <h2 className="modal-title">Criar Nova Notificação</h2>
        <p className="modal-subtitle">Configure uma nova notificação para o sistema</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="notif-type">Tipo de Notificação *</label>
            <select
              id="notif-type"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as NotificationType)}
              className="form-control type-selector"
              required
            >
              <option value="">-- Selecione o tipo --</option>
              {Object.entries(NOTIFICATION_TYPE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            {selectedType && (
              <div className="type-description">
                <Info size={14} />
                <span>{NOTIFICATION_TYPE_DESCRIPTIONS[selectedType]}</span>
              </div>
            )}
          </div>

          {showDaysOffset && (
            <div className="form-group">
              <label htmlFor="days-offset">{TYPE_DAYS_OFFSET_CONFIG[selectedType!].label}</label>
              <input
                id="days-offset"
                type="number"
                min="0"
                max="365"
                value={daysOffset}
                onChange={(e) => setDaysOffset(parseInt(e.target.value, 10) || 0)}
                className="form-control days-input"
              />
              <small className="form-hint">
                Quantos dias antes do evento a notificação será enviada.
              </small>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="notif-title">Título da Notificação *</label>
            <input
              id="notif-title"
              type="text"
              placeholder="Ex: Vencimento de Fatura"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="notif-desc">Descrição (interno)</label>
            <textarea
              id="notif-desc"
              placeholder="Descreva o propósito desta notificação (uso interno)"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="form-group">
            <h3 className="section-title">Estrutura do Email</h3>
            <label htmlFor="notif-image">Imagem (URL)</label>
            <input
              id="notif-image"
              type="url"
              placeholder="https://exemplo.com/logo.png"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="notif-intro">Introdução</label>
            <input
              id="notif-intro"
              type="text"
              placeholder="Olá {{customerName}}"
              value={introduction}
              onChange={(e) => setIntroduction(e.target.value)}
            />
            <small className="form-hint">Use {"{{customerName}}"} para inserir o nome do cliente.</small>
          </div>

          <div className="form-group">
            <label htmlFor="notif-message">Mensagem Principal *</label>
            <textarea
              id="notif-message"
              placeholder="Digite a mensagem principal do email"
              rows={4}
              value={mainMessage}
              onChange={(e) => setMainMessage(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="notif-conclusion">Conclusão</label>
            <input
              id="notif-conclusion"
              type="text"
              placeholder="Atenciosamente, Equipe Cowonder"
              value={conclusion}
              onChange={(e) => setConclusion(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="notif-contact">Link de Contato</label>
            <input
              id="notif-contact"
              type="url"
              placeholder="https://cowonder.com/contato"
              value={contactLink}
              onChange={(e) => setContactLink(e.target.value)}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={onClose} disabled={submitting}>
              Cancelar
            </button>
            <button type="submit" className="submit-button" disabled={submitting}>
              {submitting ? "Criando..." : "Criar Notificação"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
